import React, { useEffect } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ClearIcon from "@mui/icons-material/Clear";
import { Typography } from "@mui/material";
import { useMutation } from "@apollo/client";
import { useDispatch } from "react-redux";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { ASSIGNED_BOOKING_TYPE, CREATE_BOOKING_TYPE, MODALS } from "../../../config/params";
import { UrgentVisitStep } from "../../../state/urgent-visit";
import { useUrgentVisitStore } from "../../../hooks/redux-hooks/urgent-visit-store/urgent-visit.store";
import { getDateEpochInTimezone } from "../../../utils/unix-timestamp";
import { parseReasonsForVisit } from "../AppointmentReviewContainer/AppointmentReviewContainer";
import { ReasonForCancelFormType } from "../../../services/reason-for-cancel/reason-for-cancel.schema";
import { VisitCancelForm } from "../../forms";
import { useAppointmentStore } from "../../../hooks/redux-hooks/appointment-store/appointment.store";
import { useModal } from "../../../context/ModalContext";
import { usePetList } from "../../../services/pet-list/pet-list.service";
import { useUpdateAppointment } from "../../../services/appointment/appointment.service";
import { useUserStore } from "../../../hooks/redux-hooks/user-store/user.store";
import { userActivityLogger } from "../../../utils/mixpanel";
import { useBookingAppointmentTypeStore } from "../../../hooks";
import { GET_APPOINTMENTS } from "../../../services/appointment-list/appointment-list.query";
import { DASHBOARD } from "../../../services/dashboard/dashboard.query";
import { toastMessageActions } from "../../../state/request";
import { BATCH_CANCEL_APPOINTMENTS } from "../../../services/appointment/batch-cancel-appointment.mutation";
import { IBatchCancelAppointmentResponse } from "../../../services/appointment/appointment.interface";

interface BatchCancelMutationData {
  batchCancelAppointments: IBatchCancelAppointmentResponse;
}

const NewCancelContainer = () => {
  const dispatch = useDispatch();
  const { cancelAppointment, appointment } = useUpdateAppointment();
  const {
    selectedVisit, groupedAppointments, setGroupedAppointments,
    setAppointmentDetails,
  } = useAppointmentStore();
  const { openModal, closeModal } = useModal();
  const { user } = useUserStore();
  const { petsActive } = usePetList();
  const {
    appointmentTypeIndicator, setBookingAppointmentType, setAppointmentTypeIndicator,
  } = useBookingAppointmentTypeStore();
  const {
    resetUrgentVisit,
    setSelectedPets: setUrgentSelectedPets,
    setStep: setUrgentStep,
    setRescheduleAppointmentId,
    setSelectedDate,
    setPetReasons,
    setPetNotes,
    setPetAppointmentMap,
  } = useUrgentVisitStore();

  const isUrgent =
    appointmentTypeIndicator === ASSIGNED_BOOKING_TYPE.URGENT_BOOKING;
  const isBatchCancel = isUrgent && (groupedAppointments?.length ?? 0) > 1;
  const headerText = isUrgent ? "Cancel Urgent Visit" : "Cancel Visit";

  const [batchCancel] = useMutation<BatchCancelMutationData>(BATCH_CANCEL_APPOINTMENTS, {
    refetchQueries: [{ query: GET_APPOINTMENTS }, { query: DASHBOARD }],
  });
  const handleCancel = () => {
    setGroupedAppointments(null);
    closeModal();
  };

  const handleBackStep = () => {
    setGroupedAppointments(null);
    openModal(MODALS.APPOINTMENT_REVIEW);
  };
  const openUrgentReschedule = () => {
    setBookingAppointmentType(CREATE_BOOKING_TYPE.URGENT_BOOKING);
    setAppointmentTypeIndicator(selectedVisit!.appointmentCreateType);
    resetUrgentVisit();

    const appointments =
      groupedAppointments && groupedAppointments.length > 1 ?
        groupedAppointments :
        null;

    if (appointments) {
      // Multi-pet reschedule: follow GroupedAppointmentCard pattern
      setSelectedDate(getDateEpochInTimezone(
        appointments[0].start_at, appointments[0].location.timeZone,
      ));
      setRescheduleAppointmentId(appointments[0].id);
      setAppointmentDetails(appointments[0]);

      const petApptMap: Record<string, string> = {};
      const groupType = appointments[0].appointmentCreateType;
      appointments
        .filter((appt) => appt.appointmentCreateType === groupType)
        .forEach((appt) => {
          petApptMap[appt.pet.id] = appt.id;
        });
      setPetAppointmentMap(petApptMap);

      const petsToSelect = (petsActive || []).filter((p) =>
        Object.keys(petApptMap).includes(p.id));
      if (petsToSelect.length > 0) {
        setUrgentSelectedPets(petsToSelect);
        setUrgentStep(UrgentVisitStep.TIME_SELECTION);
        appointments
          .filter((appt) => appt.appointmentCreateType === groupType)
          .forEach((appt) => {
            if (appt.reasonsForVisit) {
              const parsed = parseReasonsForVisit(appt.reasonsForVisit);
              setPetReasons(appt.pet.id, parsed.reasons);
              setPetNotes(appt.pet.id, parsed.notes);
            }
          });
      }
    } else {
      // Single-pet reschedule
      setSelectedDate(getDateEpochInTimezone(
        selectedVisit!.start_at, selectedVisit!.location.timeZone,
      ));
      setRescheduleAppointmentId(selectedVisit!.id);
      const petToSelect = (petsActive || []).find(
        (p) => p.id === selectedVisit!.pet.id,
      );
      if (petToSelect) {
        setUrgentSelectedPets([petToSelect]);
        setUrgentStep(UrgentVisitStep.TIME_SELECTION);
        if (selectedVisit!.reasonsForVisit) {
          const parsed = parseReasonsForVisit(selectedVisit!.reasonsForVisit);
          setPetReasons(petToSelect.id, parsed.reasons);
          setPetNotes(petToSelect.id, parsed.notes);
        }
      }
    }
    openModal(MODALS.URGENT_VISIT_MODAL);
  };

  const handleReScheduleVisit = () => {
    if (selectedVisit?.appointmentCreateType === ASSIGNED_BOOKING_TYPE.URGENT_BOOKING) {
      openUrgentReschedule();
    } else if (selectedVisit?.appointmentCreateType === ASSIGNED_BOOKING_TYPE.EXPRESS_BOOKING) {
      openModal(MODALS.RESCHEDULE_EXPRESS_VISIT_MODAL);
    } else {
      openModal(MODALS.RESCHEDULE_A_VISIT);
    }
  };

  const handleLocation = () => {
    if (selectedVisit?.appointmentCreateType === ASSIGNED_BOOKING_TYPE.URGENT_BOOKING) {
      openUrgentReschedule();
    } else if (selectedVisit?.appointmentCreateType === ASSIGNED_BOOKING_TYPE.EXPRESS_BOOKING) {
      openModal(MODALS.RESCHEDULE_EXPRESS_VISIT_MODAL);
    } else {
      openModal(MODALS.RESCHEDULE_A_VISIT);
    }
  };

  const getAppointmentTypeText = () =>{
    switch (appointmentTypeIndicator) {
    case ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING:
      return "drop_off";
    case ASSIGNED_BOOKING_TYPE.EXPRESS_BOOKING:
      return "express_visit";
    case ASSIGNED_BOOKING_TYPE.URGENT_BOOKING:
      return "urgent_visit";
    default:
      return "in_room_visit";
    }
  };

  const handlesubmit = async (data: ReasonForCancelFormType) => {
    if (isBatchCancel && groupedAppointments) {
      dispatch(toastMessageActions.setGlobalLoading(true));

      groupedAppointments.forEach((visit) => {
        userActivityLogger(MIXPANEL_EVENTS.CANCEL_VISIT, {
          pet_name: visit.pet.name,
          appointment_id: visit.id,
          location: user?.location?.locationName,
          msa: user?.location?.city,
          owner_type:
            (petsActive?.length ?? 0) <= 1 ? "single_pet" : "multipet",
          cancellation_reason: data.reason,
          appt_type: getAppointmentTypeText(),
        });
      });

      try {
        const { data: responseData } = await batchCancel({
          variables: {
            data: {
              appointmentIds: groupedAppointments.map((v) => v.id),
              cancellation_reason: 1,
              cancellation_reason_text: data.other || data.reason,
            },
          },
        });

        const response = responseData?.batchCancelAppointments;

        if (!response) {
          throw new Error("Something went wrong. Please try again.");
        }

        const { cancelledIds, failedIds, results } = response;

        if (failedIds.length === 0) {
          dispatch(
            toastMessageActions.setGlobalAlert({
              severity: "success",
              message: "Successfully cancelled appointments.",
            }),
          );
        } else if (cancelledIds.length > 0) {
          const failedNames = results
            .filter((r) => !r.success)
            .map((r) => r.petName)
            .filter(Boolean)
            .join(", ");
          dispatch(
            toastMessageActions.setGlobalAlert({
              severity: "warning",
              message:
                `Cancelled ${cancelledIds.length} of ` +
                `${cancelledIds.length + failedIds.length}. ` +
                `Could not cancel: ${failedNames}`,
            }),
          );
        } else {
          dispatch(
            toastMessageActions.setGlobalAlert({
              severity: "error",
              message:
                "Failed to cancel appointments. Please try again.",
            }),
          );
        }
      } catch {
        dispatch(
          toastMessageActions.setGlobalAlert({
            severity: "error",
            message: "Something went wrong. Please try again.",
          }),
        );
      } finally {
        dispatch(toastMessageActions.setGlobalLoading(false));
        setGroupedAppointments(null);
        closeModal();
      }
    } else {
      userActivityLogger(MIXPANEL_EVENTS.CANCEL_VISIT, {
        pet_name: selectedVisit?.pet.name,
        appointment_id: selectedVisit?.id,
        location: user?.location?.locationName,
        msa: user?.location?.city,
        owner_type: (petsActive?.length ?? 0) <= 1 ? "single_pet" : "multipet",
        cancellation_reason: data.reason,
        appt_type: getAppointmentTypeText(),
      });
      cancelAppointment(data);
    }
  };

  useEffect(() => {
    if (appointment) {
      closeModal();
    }
  }, [appointment]);

  return (
    <>
      <div className="grow-0 shrink p-1">
        <div className="flex justify-between w-full">
          <span onClick={handleBackStep}>
            <ArrowBackIcon className="cursor-pointer" />
          </span>
          <span onClick={handleCancel}>
            <ClearIcon className="cursor-pointer" />
          </span>
        </div>
        <Typography className="!mb-6 text-center !text-2xl !font-semibold
        !grenette-pro">{headerText}</Typography>
      </div>

      <div className="flex flex-col grow shrink px-4">
        <VisitCancelForm
          onSubmit={handlesubmit}
          onReschedule={handleReScheduleVisit}
          onLocation={handleLocation}
        />
      </div>
    </>
  );
};

export default NewCancelContainer;
